import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { User } from '../../types';

export type Role = 'system_admin' | 'admin' | 'user' | 'rag_user';

interface RouteGuardProps {
    children: ReactNode | ((props: { user: User }) => ReactNode);
    roles?: ReadonlyArray<Role>;
    requireSystemOrg?: boolean;
}

export const RouteGuard = ({ children, roles, requireSystemOrg = false }: RouteGuardProps) => {
    const { user, isLoading, isAuthenticated } = useAuth();
    const location = useLocation();

    // Add detailed logging
    console.log('RouteGuard execution', {
        path: location.pathname,
        isLoading,
        isAuthenticated,
        userRole: user?.role,
        requiredRoles: roles,
        isPublicRoute: ['/login', '/signup', '/unauthorized'].includes(location.pathname)
    });

    if (isLoading) {
        console.log('RouteGuard - Loading state');
        // You could return a loading spinner here
        return null;
    }

    const isPublicRoute = ['/login', '/signup', '/unauthorized'].includes(location.pathname);

    if (isPublicRoute) {
        if (isAuthenticated && user) {
            let defaultRoute;
            if (user.role === 'system_admin') {
                defaultRoute = '/organizations';
            } else if (user.role === 'user') {
                defaultRoute = '/rag-applications';
            } else {
                defaultRoute = '/dashboard';
            }
            return <Navigate to={defaultRoute} replace />;
        }
        console.log('RouteGuard - Rendering public route content');
        return <>{children}</>;
    }

    console.log('RouteGuard - Protected route check', {
        path: location.pathname,
        isAuthenticated,
        hasUser: !!user
    });

    if (!isAuthenticated || !user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (requireSystemOrg && !user.isSystemOrg) {
        return <Navigate to="/unauthorized" replace />;
    }

    if (roles && !roles.includes(user.role)) {
        if (user.role === 'system_admin') {
            return <Navigate to="/organizations" replace />;
        }
        return <Navigate to="/unauthorized" replace />;
    }

    return (
        <>
            {typeof children === 'function' ? children({ user }) : children}
        </>
    );
};